import React from 'react'
import cx from 'classnames'
import moment from 'moment'
import { t } from 'ttag'
import { Icon } from 'components/shared/Icon/Icon'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  GridIcon,
  RefreshCwIcon,
} from 'components/shared/Icon/model'
import {
  DominantPatternIcon,
  KeyContrastIcon,
  WatchForIcon,
} from './ObservationIcons'
import { Button } from 'components/shared/Button/Button'
import {
  formatTraitAbstractFirstSentence,
  getRecruiterTraits,
} from 'components/pages/Help/helpScoreCardText'
import {
  BIG_FIVE_FACTORS,
  ZONE,
  ZONE_ORDER,
  getInitials,
  getKeyObservations,
  getZoneLabel,
  groupTalentsByZone,
  orderTalents,
  withTalentZones,
} from '../../utils'
import {
  DEFAULT_TABLE_ORDERING,
  VALUE_LEVEL_COLUMN,
  getDefaultSortDirection,
  getJobblinkDataTableColumns,
} from '../TalentAreasTable/model'
import { DataTableColumnSort } from './DataTableColumnSort'
import { TalentAreaInfoModal } from './TalentAreaInfoModal'
import styles from './JobblinkOverview.module.scss'

const getZoneSections = () => [
  {
    zone: ZONE.CORE,
    title: t`Core talents`,
    subtitle: t`high match`,
  },
  {
    zone: ZONE.POTENTIAL,
    title: t`Potential`,
    subtitle: t`medium match`,
  },
  {
    zone: ZONE.REQUIRES_ENERGY,
    title: t`Requires more energy`,
    subtitle: t`low match`,
  },
]

const getObservationItems = () => [
  {
    key: 'dominantPattern',
    title: t`Dominant pattern`,
    IconComponent: DominantPatternIcon,
  },
  {
    key: 'watchFor',
    title: t`Watch for`,
    IconComponent: WatchForIcon,
  },
  {
    key: 'keyContrast',
    title: t`Key contrast`,
    IconComponent: KeyContrastIcon,
  },
]

const TalentCard = ({ area, matchScore, zone, onClick }) => (
  <button
    type="button"
    className={cx(styles.talentCard, styles[`talentCard_${zone}`])}
    onClick={onClick}
  >
    <div className={styles.talentCardAccent} aria-hidden="true" />
    <div className={styles.talentCardContent}>
      <div className={styles.talentCardHeader}>
        <span className={styles.talentName}>{area}</span>
        <span className={styles.talentScore}>{matchScore}</span>
      </div>
      <div className={styles.talentBarTrack}>
        <div
          className={styles.talentBarFill}
          style={{ width: `${matchScore}%` }}
        />
      </div>
    </div>
  </button>
)

const TalentZoneSection = ({
  zone,
  title,
  subtitle,
  talents,
  onTalentClick,
}) => {
  if (!talents.length) {
    return null
  }

  return (
    <section className={styles.zoneSection}>
      <h3 className={styles.zoneHeader}>
        <span className={styles.zoneTitle}>{title}</span>
        <span className={styles.zoneSubtitle}> – {subtitle}</span>
      </h3>
      <div className={styles.talentGrid}>
        {talents.map(talent => (
          <TalentCard
            key={talent.code}
            {...talent}
            zone={zone}
            onClick={() => onTalentClick(talent)}
          />
        ))}
      </div>
    </section>
  )
}

const FullDataTable = ({ talents, onTalentClick }) => {
  const [isOpen, setIsOpen] = React.useState(true)
  const [ordering, setOrdering] = React.useState(DEFAULT_TABLE_ORDERING)
  const columns = React.useMemo(() => getJobblinkDataTableColumns(), [])

  const sortedTalents = React.useMemo(
    () => orderTalents(talents, ordering),
    [talents, ordering],
  )

  const setOrder = React.useCallback((direction, field) => {
    setOrdering({
      ascending: direction === 'ascending' ? field : null,
      descending: direction === 'descending' ? field : null,
    })
  }, [])

  const handleSortClick = React.useCallback(
    (field, direction) => {
      const activeField = ordering.descending ?? ordering.ascending
      const sortDirection =
        activeField === field ? direction : getDefaultSortDirection(field)

      setOrder(sortDirection, field)
    },
    [ordering, setOrder],
  )

  const resetSorting = React.useCallback(() => {
    setOrdering(DEFAULT_TABLE_ORDERING)
  }, [])

  const isDefaultSort =
    ordering.descending === VALUE_LEVEL_COLUMN && !ordering.ascending

  return (
    <section className={styles.dataTableSection}>
      <div className={styles.dataTableToggleCard}>
        <button
          type="button"
          className={styles.dataTableToggle}
          onClick={() => setIsOpen(open => !open)}
        >
          <Icon className={styles.dataTableIcon} icon={GridIcon} size={16} />
          <span className={styles.dataTableToggleLabel}>
            {t`Full data table - all ${talents.length} talent areas`}
          </span>
          {isOpen ? (
            <div className={styles.dataTableToggleTrailing}>
              {/* keeps the space the reset button is placed over */}
              <div className={styles.dataTableResetSlot} />
              <Icon
                className={styles.dataTableChevron}
                icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
                size={16}
              />
            </div>
          ) : (
            <Icon
              className={styles.dataTableChevron}
              icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
              size={16}
            />
          )}
        </button>
        {/* a sibling of the toggle, because a button cannot hold a button */}
        {isOpen && !isDefaultSort ? (
          <Button
            className={styles.dataTableResetSort}
            icon={RefreshCwIcon}
            label={t`Reset sorting`}
            size="xsmall"
            variant="outlined"
            onClick={resetSorting}
          />
        ) : null}
      </div>
      {isOpen ? (
        <div className={styles.dataTableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                {columns.map(({ header, field, numeric }) => (
                  <th
                    key={field}
                    className={numeric ? styles.numericHeader : undefined}
                  >
                    <div
                      className={cx(styles.dataTableHeaderCell, {
                        [styles.dataTableHeaderCellNumeric]: numeric,
                      })}
                    >
                      <span className={styles.dataTableHeaderLabel}>
                        {header}
                      </span>
                      <DataTableColumnSort
                        isDescending={
                          ordering.descending === field
                            ? true
                            : ordering.ascending === field
                              ? false
                              : null
                        }
                        onAscending={() => handleSortClick(field, 'ascending')}
                        onDescending={() =>
                          handleSortClick(field, 'descending')
                        }
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedTalents.map(talent => (
                <tr
                  key={talent.code}
                  className={cx(
                    styles.dataTableRow,
                    styles[`row_${talent.zone}`],
                  )}
                  onClick={() => onTalentClick(talent)}
                >
                  <td>{talent.area}</td>
                  <td>
                    <span className={styles.zoneCell}>
                      <span
                        className={cx(
                          styles.zoneDot,
                          styles[`zoneDot_${talent.zone}`],
                        )}
                      />
                      {getZoneLabel(talent.zone)}
                    </span>
                  </td>
                  <td className={styles.numericCell}>
                    {talent.personalityScore}
                  </td>
                  <td className={styles.numericCell}>{talent.interestScore}</td>
                  <td className={styles.numericCell}>{talent.matchScore}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

type JobblinkOverviewProps = {
  jobSeeker: any
  candidateResponse: any
  talents: any[]
  language?: string
  showGmaToggle?: boolean
  showGma?: boolean
  setShowGma?: (show: boolean) => void
}

export const JobblinkOverview = ({
  jobSeeker,
  candidateResponse,
  talents,
  language,
  showGmaToggle,
  showGma,
  setShowGma,
}: JobblinkOverviewProps) => {
  const [isBigFiveExpanded, setIsBigFiveExpanded] = React.useState(false)
  const [selectedTalent, setSelectedTalent] = React.useState(null)

  React.useEffect(() => {
    setIsBigFiveExpanded(false)
    setSelectedTalent(null)
  }, [jobSeeker?.id])

  const zoneSections = React.useMemo(() => getZoneSections(), [language])
  const observationItems = React.useMemo(
    () => getObservationItems(),
    [language],
  )

  const zonedTalents = React.useMemo(() => withTalentZones(talents), [talents])
  const groupedTalents = React.useMemo(
    () => groupTalentsByZone(zonedTalents),
    [zonedTalents],
  )
  const tableTalents = React.useMemo(
    () => ZONE_ORDER.flatMap(zone => groupedTalents[zone]).filter(Boolean),
    [groupedTalents],
  )
  const observations = React.useMemo(
    () =>
      getKeyObservations({
        scores: candidateResponse?.scores,
        talents: zonedTalents,
        language,
      }),
    [candidateResponse?.scores, zonedTalents, language],
  )
  const bigFiveScores = React.useMemo(() => {
    const traitDefinitions = getRecruiterTraits(language)

    return BIG_FIVE_FACTORS.map(({ key, label }) => {
      const scoreValue = candidateResponse?.scores?.[key] ?? 0
      const traitDef = traitDefinitions[key] || {}
      const traitLabel = traitDef.label || label()

      return {
        key,
        label: traitLabel,
        score: Math.round(scoreValue * 100),
        description: formatTraitAbstractFirstSentence(
          traitDef.abstract,
          traitLabel,
        ),
      }
    }).sort((a, b) => b.score - a.score)
  }, [candidateResponse?.scores, language])

  const completedAt = candidateResponse?.timestamps?.completed
  const completedLabel = completedAt
    ? moment(completedAt)
        .locale(language === 'nb' ? 'nb' : 'en')
        .format('MMMM YYYY')
    : t`Not completed`

  return (
    <div className={styles.overview}>
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.avatar}>{getInitials(jobSeeker?.name)}</div>
          <div className={styles.headerText}>
            <h2 className={styles.candidateName}>{jobSeeker?.name}</h2>
            <p className={styles.meta}>
              {t`Test completed ${completedLabel}`} ·{' '}
              {t`${talents.length} talent areas`}
            </p>
          </div>
        </div>
        {showGmaToggle ? (
          <div className={styles.gmaToggle}>
            <Button
              label={t`Without GMA`}
              className={cx(styles.gmaButton, {
                [styles.gmaButtonActive]: !showGma,
              })}
              variant={!showGma ? 'primary filled' : 'outlined'}
              onClick={() => setShowGma!(false)}
            />
            <Button
              label={t`With GMA`}
              className={cx(styles.gmaButton, {
                [styles.gmaButtonActive]: showGma,
              })}
              variant={showGma ? 'primary filled' : 'outlined'}
              onClick={() => setShowGma!(true)}
            />
          </div>
        ) : null}
      </header>

      <section className={styles.observationsSection}>
        <h3 className={styles.sectionEyebrow}>
          {t`Key observations before the session`}
        </h3>
        <div className={styles.observationsCard}>
          {observationItems.map(({ key, title, IconComponent }) => (
            <div key={key} className={styles.observationRow}>
              <div className={styles.observationIconWrap}>
                <IconComponent size={16} />
              </div>
              <div className={styles.observationContent}>
                <div className={styles.observationTitle}>{title}</div>
                <p className={styles.observationText}>{observations[key]}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.bigFiveSection}>
        <h3 className={styles.sectionEyebrow}>{t`Big Five - Personality`}</h3>
        <div
          className={cx(styles.bigFiveCard, {
            [styles.bigFiveCardExpanded]: isBigFiveExpanded,
          })}
        >
          {isBigFiveExpanded ? (
            <div className={styles.bigFiveExpanded}>
              {bigFiveScores.map(({ key, label, score, description }) => (
                <div key={key} className={styles.bigFiveDetail}>
                  <div className={styles.bigFiveDetailHeader}>
                    <span className={styles.bigFiveDetailLabel}>{label}</span>
                    <span className={styles.bigFiveDetailScore}>{score}%</span>
                  </div>
                  {description ? (
                    <p className={styles.bigFiveDetailText}>{description}</p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.bigFiveGrid}>
              {bigFiveScores.map(({ key, label, score }) => (
                <div key={key} className={styles.bigFiveItem}>
                  <div className={styles.bigFiveLabel}>{label}</div>
                  <div className={styles.bigFiveBarRow}>
                    <div className={styles.bigFiveBarTrack}>
                      <div
                        className={styles.bigFiveBarFill}
                        style={{ width: `${score}%` }}
                      />
                    </div>
                    <span className={styles.bigFiveScore}>{score}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            className={styles.expandButton}
            onClick={() => setIsBigFiveExpanded(expanded => !expanded)}
            aria-expanded={isBigFiveExpanded}
            aria-label={
              isBigFiveExpanded
                ? t`Collapse personality details`
                : t`Expand personality details`
            }
          >
            <Icon
              icon={isBigFiveExpanded ? ChevronUpIcon : ChevronDownIcon}
              size={18}
            />
          </button>
        </div>
      </section>

      <div className={styles.zoneSections}>
        {zoneSections.map(section => (
          <TalentZoneSection
            key={section.zone}
            {...section}
            talents={groupedTalents[section.zone]}
            onTalentClick={setSelectedTalent}
          />
        ))}
      </div>

      <FullDataTable talents={tableTalents} onTalentClick={setSelectedTalent} />

      <TalentAreaInfoModal
        talent={selectedTalent}
        onClose={() => setSelectedTalent(null)}
      />
    </div>
  )
}
